import type { SupportedStateCode } from '@/common/constants/states';
import type { ContactsRequiredFields } from '../shared/schema';

/**
 * Per-state behavior of the Offer contacts form: a flat `DEFAULT_CONFIG` plus
 * per-state deltas mirroring the legacy cover-sheet offer overrides in
 * `views/Coversheet/configurations/offer/overrides/*`. The user represents the
 * BUYER, so the own-side `agent`/`client` are the buyer's and the counterparty
 * side is the seller's.
 */
export interface OfferContactsConfig {
  // Buyer side
  showBuyersAttorney: boolean;
  /** Brokerage MLS code on the agent card. */
  showBuyerAgentMlsCode: boolean;
  /** Extended agent MLS/brokerage identifiers beyond the shared license block. */
  showBuyerAgentOfficeEmail: boolean;
  showBuyerAgentPrimaryMls: boolean;
  showBuyerAgentMlsId: boolean;
  showBuyerAgentBrokerageMlsId: boolean;
  showBuyerIntermediaryBanner: boolean;
  /** Adds an "Ownership Status" select to the buyer card. */
  showBuyerOwnershipStatus: boolean;

  // Seller side
  showSellersAttorney: boolean;
  showSellerAgentMlsCode: boolean;
  showSellerAgentOfficeEmail: boolean;
  showSellerAgentPrimaryMls: boolean;
  showSellerAgentMlsId: boolean;
  showSellerAgentBrokerageMlsId: boolean;
  showSellerIntermediaryBanner: boolean;
  /** When false, the seller's name is not required. */
  sellerNameRequired: boolean;
  /** When false, the seller's agent's license and brokerage are not required. */
  sellerAgentLicenseRequired: boolean;
  sellerAgentBrokerLicenseRequired: boolean;
  sellerAgentBrokerNameRequired: boolean;

  // Attorney cards
  /**
   * Empty buyer's/seller's attorney cards seeded when absent, so the form opens
   * an editable card instead of a bare "Add" button.
   */
  minAttorneyCards: number;

  // Additional contacts
  showTitleOfficer: boolean;
  /** When false, the title officer card omits the Title Number/GFN field. */
  showTitleNumber: boolean;
  showEscrowOfficer: boolean;
  showAttorneySection: boolean;
  showClosingAttorneySection: boolean;
  showPropertyManagingAgentSection: boolean;
}

const DEFAULT_CONFIG: OfferContactsConfig = {
  showBuyersAttorney: false,
  showBuyerAgentMlsCode: false,
  showBuyerAgentOfficeEmail: false,
  showBuyerAgentPrimaryMls: false,
  showBuyerAgentMlsId: false,
  showBuyerAgentBrokerageMlsId: false,
  showBuyerIntermediaryBanner: false,
  showBuyerOwnershipStatus: false,

  showSellersAttorney: false,
  showSellerAgentMlsCode: false,
  showSellerAgentOfficeEmail: false,
  showSellerAgentPrimaryMls: false,
  showSellerAgentMlsId: false,
  showSellerAgentBrokerageMlsId: false,
  showSellerIntermediaryBanner: false,
  sellerNameRequired: true,
  sellerAgentLicenseRequired: true,
  sellerAgentBrokerLicenseRequired: true,
  sellerAgentBrokerNameRequired: true,

  minAttorneyCards: 0,

  showTitleOfficer: true,
  showTitleNumber: true,
  showEscrowOfficer: false,
  showAttorneySection: true,
  showClosingAttorneySection: false,
  showPropertyManagingAgentSection: false,
};

export type OfferContactsConfigDelta = Partial<OfferContactsConfig>;

/** Per-state deltas merged onto `DEFAULT_CONFIG`; top-level keys replace. */
const STATE_CONFIGS: Partial<
  Record<SupportedStateCode, OfferContactsConfigDelta>
> = {
  CA: {
    showEscrowOfficer: true,
  },
  DC: {
    sellerAgentLicenseRequired: false,
    sellerAgentBrokerLicenseRequired: false,
    sellerAgentBrokerNameRequired: false,
  },
  FL: {
    showTitleNumber: false,
  },
  GA: {
    showBuyerAgentMlsCode: true,
    showSellerAgentMlsCode: true,
    sellerNameRequired: false,
    showTitleOfficer: false,
    showAttorneySection: false,
    showClosingAttorneySection: true,
  },
  MA: {
    showBuyersAttorney: true,
    showSellersAttorney: true,
    showTitleOfficer: false,
    showAttorneySection: false,
    sellerAgentLicenseRequired: false,
    sellerAgentBrokerLicenseRequired: false,
    sellerAgentBrokerNameRequired: false,
  },
  MD: {
    sellerAgentLicenseRequired: false,
    sellerAgentBrokerLicenseRequired: false,
    sellerAgentBrokerNameRequired: false,
  },
  NC: {
    showTitleOfficer: false,
    showAttorneySection: false,
    showClosingAttorneySection: true,
  },
  NJ: {
    showSellersAttorney: true,
    showAttorneySection: false,
  },
  NY: {
    showBuyersAttorney: true,
    showSellersAttorney: true,
    minAttorneyCards: 1,
    showTitleOfficer: false,
    showAttorneySection: false,
    showPropertyManagingAgentSection: true,
  },
  OR: {
    showTitleOfficer: false,
    showEscrowOfficer: true,
  },
  SC: {
    showTitleOfficer: false,
    showAttorneySection: false,
    showClosingAttorneySection: true,
  },
  TX: {
    showBuyerIntermediaryBanner: true,
    showSellerIntermediaryBanner: true,
  },
  VA: {
    sellerAgentLicenseRequired: false,
    sellerAgentBrokerLicenseRequired: false,
    sellerAgentBrokerNameRequired: false,
  },
  WA: {
    showEscrowOfficer: true,
    showBuyerAgentOfficeEmail: true,
    showBuyerAgentPrimaryMls: true,
    showBuyerAgentMlsId: true,
    showBuyerAgentBrokerageMlsId: true,
    showSellerAgentOfficeEmail: true,
    showSellerAgentPrimaryMls: true,
    showSellerAgentMlsId: true,
    showSellerAgentBrokerageMlsId: true,
    showBuyerOwnershipStatus: true,
  },
};

/**
 * Build an `OfferContactsConfig` from a delta against `DEFAULT_CONFIG`.
 * Called with no argument returns the unmodified defaults.
 */
export const buildOfferContactsConfig = (
  delta: OfferContactsConfigDelta = {},
): OfferContactsConfig => ({
  ...DEFAULT_CONFIG,
  ...delta,
});

/** Resolve the per-state Offer contacts config. */
export const resolveOfferContactsConfig = (
  state: SupportedStateCode,
): OfferContactsConfig => buildOfferContactsConfig(STATE_CONFIGS[state]);

/**
 * The offer contacts' required name/email rules, derived from the resolved
 * config. Single source shared by the validation schema (sidebar badge +
 * lockdown) and the form's asterisk/highlight. The user represents the buyer,
 * so the buyer's own agent isn't required; the buyer (client) is; the seller's
 * agent needs a name; the seller's name is required except where the config
 * drops it.
 */
export const getRequiredContactFields = (
  config: OfferContactsConfig,
): ContactsRequiredFields => ({
  agent: { firstName: false, lastName: false, email: false },
  counterpartyAgent: {
    firstName: true,
    lastName: true,
    email: false,
    licenseNumber: config.sellerAgentLicenseRequired,
    brokerageName: config.sellerAgentBrokerNameRequired,
    brokerageLicenseNumber: config.sellerAgentBrokerLicenseRequired,
  },
  client: { firstName: true, lastName: true, email: true },
  counterpartyClient: {
    firstName: config.sellerNameRequired,
    lastName: false,
    email: false,
  },
});
